import { useState, useRef, useEffect } from "react";
import { PenTool, Type, RotateCcw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface SignaturePadProps {
  onSignatureChange: (signatureData: string) => void;
  signerName: string;
}

const signatureFonts = [
  { name: "Cursive", className: "font-serif italic" },
  { name: "Script", className: "font-script" },
  { name: "Formal", className: "font-serif" },
];

export function SignaturePad({ onSignatureChange, signerName }: SignaturePadProps) {
  const [mode, setMode] = useState<"draw" | "type">("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set up canvas for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    // Draw signature line
    ctx.strokeStyle = "#e5e5e5";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();

    // Add "Sign here" text
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "12px sans-serif";
    ctx.fillText("Sign above the line", 20, rect.height - 10);
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    setHasDrawn(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      updateSignature();
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Redraw signature line
    ctx.strokeStyle = "#e5e5e5";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();

    // Redraw hint text
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "12px sans-serif";
    ctx.fillText("Sign above the line", 20, rect.height - 10);

    setHasDrawn(false);
    onSignatureChange("");
  };

  const updateSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const signatureData = canvas.toDataURL("image/png");
    onSignatureChange(signatureData);
  };

  const generateTypedSignature = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 500;
    canvas.height = 150;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw signature line
    ctx.strokeStyle = "#e5e5e5";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();

    // Draw typed signature
    ctx.fillStyle = "#1a1a1a";
    const fontSize = selectedFont === 1 ? 40 : 36;
    const fontStyle = selectedFont === 0 ? "italic" : selectedFont === 1 ? "italic" : "";
    ctx.font = `${fontStyle} ${fontSize}px Georgia, serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typedSignature || signerName, canvas.width / 2, canvas.height / 2 - 10);

    return canvas.toDataURL("image/png");
  };

  useEffect(() => {
    if (mode === "type" && (typedSignature || signerName)) {
      const signatureData = generateTypedSignature();
      onSignatureChange(signatureData);
    }
  }, [mode, typedSignature, selectedFont, signerName]);

  return (
    <div className="space-y-4">
      <Tabs value={mode} onValueChange={(v) => setMode(v as "draw" | "type")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="draw" className="flex items-center gap-2">
            <PenTool className="h-4 w-4" />
            Draw Signature
          </TabsTrigger>
          <TabsTrigger value="type" className="flex items-center gap-2">
            <Type className="h-4 w-4" />
            Type Signature
          </TabsTrigger>
        </TabsList>

        <TabsContent value="draw" className="mt-4">
          <div className="border rounded-lg bg-white overflow-hidden shadow-inner">
            <canvas
              ref={canvasRef}
              className="w-full h-[150px] cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-muted-foreground">
              Use your mouse or finger to draw your signature
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCanvas}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="type" className="mt-4 space-y-4">
          <div className="space-y-2">
            <Input
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              placeholder={signerName || "Type your signature"}
              className="text-lg"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Select signature style:</p>
            <div className="grid grid-cols-3 gap-2">
              {signatureFonts.map((font, index) => (
                <button
                  key={font.name}
                  onClick={() => setSelectedFont(index)}
                  className={cn(
                    "p-4 border rounded-lg transition-all bg-white",
                    selectedFont === index
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <span
                    className={cn(
                      "text-xl text-foreground block truncate",
                      font.className
                    )}
                  >
                    {typedSignature || signerName || "Signature"}
                  </span>
                  <span className="text-xs text-muted-foreground mt-1 block">
                    {font.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="border rounded-lg bg-white p-6 shadow-inner">
            <div className="border-b border-muted pb-4">
              <p
                className={cn(
                  "text-3xl text-center text-foreground",
                  signatureFonts[selectedFont].className
                )}
              >
                {typedSignature || signerName || "Your Signature"}
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Preview of your typed signature
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {((mode === "draw" && hasDrawn) || (mode === "type" && (typedSignature || signerName))) && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <Check className="h-4 w-4" />
          <span>Signature captured</span>
        </div>
      )}
    </div>
  );
}
